"use client";

import { useEffect, useRef } from "react";

/*
  Telegram Mini App auto-login (CENTRAL IDENTITY via accounts.owlcat).

  When Filmcrew runs INSIDE a Telegram Mini App, the Telegram WebApp SDK exposes
  a signed `initData` string on window.Telegram.WebApp.initData. This component
  POSTs it to /api/auth/telegram/miniapp, which verifies the HMAC locally with
  film's bot token, token-exchanges a signed app assertion with accounts (verify
  → identity by tgId → JIT film user by identityId), sets film's native session
  cookie + the shared owlcat-id cookie, and returns JSON. On success we reload so
  the app reads the fresh session.

  Fallbacks: no initData, or the exchange fails → bounce to film's normal login
  (/signin) so the user can still sign in via Google / Login Widget. Outside
  Telegram, window.Telegram is undefined, so this is a no-op — safe to mount
  anywhere. One-shot per mount.
*/

interface TelegramWebApp {
  initData?: string;
  ready?: () => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function TelegramMiniAppAuth({ callbackUrl = "/" }: { callbackUrl?: string }) {
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    const initData = window.Telegram?.WebApp?.initData;
    if (!initData) return; // not inside Telegram — no-op
    attempted.current = true;
    window.Telegram?.WebApp?.ready?.();

    const bounceToLogin = () => {
      window.location.href = `/?next=${encodeURIComponent(callbackUrl)}`;
    };

    void (async () => {
      try {
        const res = await fetch("/api/auth/telegram/miniapp", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ initData }),
        });
        if (!res.ok) {
          bounceToLogin();
          return;
        }
        // Native session cookie is set — reload so the app reads the new session.
        try { sessionStorage.setItem('film-signin-method', 'miniapp'); } catch { /* ignore */ }
        window.location.reload();
      } catch {
        bounceToLogin();
      }
    })();
  }, [callbackUrl]);

  return null;
}
